// Shared ftcore browser utils that portal pages need. `ftcore.bundle.js` covers Desk; a web
// page gets none of it, so anything a portal recomputes client-side is imported here.
// `floor_at_zero` is the supplier portal's half of the totals arithmetic — see
// ftcore/utils/amounts.py for why it is one function rather than a Math.max per subtraction.
import "../ftcore/js/utils/amount_utils.js";

// Moving a portal's status pill once an answer is recorded, so a supplier is not left reloading to
// find out whether we heard them. Both purchasing portals use it; see the file for why it only ever
// renders a value the server handed back.
import "../ftcore/js/utils/portal_status_utils.js";

// Global trackers
window.cur_dialog = null;
if (!frappe.ui) frappe.provide("frappe.ui");
frappe.ui.open_dialogs = [];


/**
 * A lightweight, reusable dialog for Frappe web portals, where the desk's `frappe.ui.Dialog` is not
 * available. The API mirrors `frappe.ui.Dialog` as closely as possible: pass a list of field
 * definitions ({ fieldtype, fieldname, label, options, reqd, default, ... }) and primary/secondary
 * actions, then read values back with get_values()/get_value(). Styling is driven by Frappe theme
 * CSS variables so it visually matches the standard desk dialog.
 *
 * Supported fieldtypes: Data, Int, Float, Password, Select, Text/Small Text, Check, Date,
 * Signature (a draw-to-sign canvas, returns a base64 PNG data URI like frappe's Signature field),
 * and the display-only Heading and HTML.
 */
class PortalDialog {
    constructor(opts = {}) {
        this.title = opts.title || "Confirm";
        this.fields = opts.fields || [];
        this.primary_action_label = opts.primary_action_label || "Confirm";
        this.secondary_action_label = opts.secondary_action_label || "Cancel";
        this.primary_action = opts.primary_action;
        this.secondary_action = opts.secondary_action;
        this.on_hide = opts.on_hide || opts.onhide;

        // Keyed by fieldname so callers can reach individual controls (mirrors frappe's fields_dict)
        this.fields_dict = {};
        this.modal_id = "portal_modal_" + Math.random().toString(36).substring(2, 9);

        this.init();
    }

    // ---- Setup -----------------------------------------------------------------------------------

    init() {
        this.inject_styles();

        const modal_html = `
            <div class="portal-backdrop" id="${this.modal_id}_backdrop"></div>
            <div class="modal portal-dialog" id="${this.modal_id}" tabindex="-1" role="dialog" style="display: none;">
                <div class="modal-dialog portal-dialog-panel portal-animate-in" id="${this.modal_id}_dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${this.escape_html(this.title)}</h5>
                            <button type="button" class="btn portal-dialog-close" aria-label="Close">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                                </svg>
                            </button>
                        </div>
                        <div class="modal-body">
                            <form id="${this.modal_id}_form" onsubmit="return false;"></form>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-default btn-sm portal-dialog-close">${this.escape_html(this.secondary_action_label)}</button>
                            <button type="button" class="btn btn-primary btn-sm" id="${this.modal_id}_primary">${this.escape_html(this.primary_action_label)}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        $("body").append(modal_html);
        this.$wrapper = $(`#${this.modal_id}`);
        this.$backdrop = $(`#${this.modal_id}_backdrop`);
        this.$dialog = $(`#${this.modal_id}_dialog`);
        this.$form = $(`#${this.modal_id}_form`);

        this.render_fields();
        this.bind_events();
    }

    inject_styles() {
        if ($("#portal-dialog-styles").length) return;

        const style_html = `
            <style id="portal-dialog-styles">
                @keyframes portal-slide-in {
                    from { transform: translate3d(0, -30px, 0); opacity: 0; }
                    to { transform: translate3d(0, 0, 0); opacity: 1; }
                }
                @keyframes portal-slide-out {
                    from { transform: translate3d(0, 0, 0); opacity: 1; }
                    to { transform: translate3d(0, -30px, 0); opacity: 0; }
                }
                .portal-animate-in { animation: portal-slide-in 0.2s ease-out forwards; }
                .portal-animate-out { animation: portal-slide-out 0.15s ease-in forwards; }

                .portal-backdrop {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: 1040; opacity: 0; transition: opacity 0.2s ease;
                }
                .portal-backdrop.show { opacity: 1; }

                .modal.portal-dialog {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    z-index: 1050; overflow-y: auto;
                }
                .portal-dialog .portal-dialog-panel {
                    /* Sit near the top and scale with the viewport, matching the desk dialog */
                    margin: 5vh auto;
                    width: 92%;
                    max-width: 720px;
                }
                .portal-dialog .modal-content {
                    border: 1px solid var(--border-color, #d1d8dd);
                    border-radius: var(--border-radius-lg, 8px);
                    background-color: var(--card-bg, var(--fg-color, #fff));
                    box-shadow: var(--shadow-2xl, 0 10px 40px rgba(0, 0, 0, 0.2));
                    color: var(--text-color, #1f272e);
                }
                .portal-dialog .modal-header {
                    align-items: center; padding: 15px var(--padding-lg, 20px);
                    border-bottom: 1px solid var(--border-color, #d1d8dd);
                }
                .portal-dialog .modal-title {
                    font-size: var(--text-lg, 1.05rem); font-weight: 600;
                    color: var(--heading-color, var(--text-color, #1f272e)); margin: 0;
                }
                .portal-dialog .portal-dialog-close {
                    background: transparent; border: none; padding: 4px;
                    color: var(--text-muted, #8d99a6); cursor: pointer; line-height: 1;
                }
                .portal-dialog .portal-dialog-close:hover { color: var(--text-color, #1f272e); }
                .portal-dialog .modal-body { padding: var(--padding-lg, 20px); }
                .portal-dialog .modal-footer {
                    justify-content: flex-end; gap: 8px;
                    padding: 12px var(--padding-lg, 20px);
                    border-top: 1px solid var(--border-color, #d1d8dd);
                }

                .portal-dialog .frappe-control { margin-bottom: var(--margin-md, 15px); }
                .portal-dialog .frappe-control:last-child { margin-bottom: 0; }
                .portal-dialog .control-label {
                    font-size: var(--text-sm, 0.85rem); font-weight: 500;
                    color: var(--text-muted, #6c7680); margin-bottom: 4px; display: block;
                }
                .portal-dialog .form-control {
                    background-color: var(--control-bg, #fff);
                    border: 1px solid var(--border-color, #d1d8dd);
                    border-radius: var(--border-radius, 6px);
                    color: var(--text-color, #1f272e);
                    font-size: var(--text-md, 0.9rem);
                }
                .portal-dialog .form-control:focus {
                    border-color: var(--primary, #2490ef);
                    box-shadow: 0 0 0 2px var(--primary-light, rgba(36, 144, 239, 0.25));
                    outline: none;
                }
                .portal-dialog .form-control.is-invalid {
                    border-color: var(--red-400, #e24c4c);
                }
                .portal-dialog .portal-check { display: flex; align-items: center; gap: 8px; }
                .portal-dialog .portal-check label { margin: 0; cursor: pointer; color: var(--text-color, #1f272e); }
                .portal-dialog .help-box {
                    font-size: var(--text-sm, 0.8rem); color: var(--text-muted, #8d99a6); margin: 4px 0 0;
                }
                .portal-dialog .portal-heading {
                    font-size: var(--text-md, 0.95rem); color: var(--text-color, #1f272e);
                    line-height: 1.5; margin-bottom: var(--margin-md, 15px);
                }
                .portal-dialog .portal-signature { position: relative; }
                .portal-dialog .portal-signature-canvas {
                    display: block; width: 100%; height: 180px;
                    background-color: var(--control-bg, #fff);
                    border: 1px solid var(--border-color, #d1d8dd);
                    border-radius: var(--border-radius, 6px);
                    touch-action: none; cursor: crosshair;
                }
                .portal-dialog .portal-signature-canvas.is-invalid { border-color: var(--red-400, #e24c4c); }
                .portal-dialog .portal-signature-clear {
                    position: absolute; top: 6px; right: 8px;
                    background: transparent; border: none; padding: 2px 6px;
                    font-size: var(--text-sm, 0.8rem); color: var(--text-muted, #8d99a6); cursor: pointer;
                }
                .portal-dialog .portal-signature-clear:hover { color: var(--text-color, #1f272e); text-decoration: underline; }
            </style>
        `;
        $("head").append(style_html);
    }

    // ---- Field rendering -------------------------------------------------------------------------

    render_fields() {
        this.fields.forEach(df => {
            const $field = $(this.make_field(df));
            this.$form.append($field);
            if (!df.fieldname) return;
            if (df.fieldtype === "Signature") {
                // The drawing pad is wired up in show(), once the canvas has real dimensions
                this.fields_dict[df.fieldname] = { df, $canvas: $field.find("canvas"), signature: null };
            } else {
                this.fields_dict[df.fieldname] = { df, $input: $field.find(`[name="${df.fieldname}"]`) };
            }
        });
    }

    make_field(df) {
        const req_star = df.reqd ? '<span class="text-danger">&nbsp;*</span>' : "";
        const label = df.label
            ? `<label class="control-label">${this.escape_html(df.label)}${req_star}</label>`
            : "";
        const description = df.description
            ? `<p class="help-box">${this.escape_html(df.description)}</p>`
            : "";
        const value = df.default != null ? df.default : (df.value != null ? df.value : "");
        const placeholder = this.escape_attr(df.placeholder || "");

        let control = "";
        switch (df.fieldtype) {
            case "Heading":
                return `<div class="frappe-control"><p class="portal-heading">${this.escape_html(df.label || "")}</p></div>`;

            case "HTML":
                return `<div class="frappe-control">${df.options || df.html || ""}</div>`;

            case "Select":
                control = `
                    <select class="form-control" name="${df.fieldname}">
                        ${this.get_select_options(df).map(opt =>
                            `<option value="${this.escape_attr(opt.value)}" ${opt.value === value ? "selected" : ""}>${this.escape_html(opt.label)}</option>`
                        ).join("")}
                    </select>`;
                break;

            case "Text":
            case "Small Text":
            case "Code":
                control = `<textarea class="form-control" name="${df.fieldname}" rows="${df.rows || 4}" placeholder="${placeholder}">${this.escape_html(value)}</textarea>`;
                break;

            case "Check":
                return `
                    <div class="frappe-control">
                        <div class="portal-check">
                            <input type="checkbox" class="form-check-input" id="${this.modal_id}_${df.fieldname}" name="${df.fieldname}" ${value ? "checked" : ""}>
                            <label for="${this.modal_id}_${df.fieldname}">${this.escape_html(df.label || "")}</label>
                        </div>
                        ${description}
                    </div>`;

            case "Int":
            case "Float":
            case "Currency":
                control = `<input type="number" class="form-control" name="${df.fieldname}" value="${this.escape_attr(value)}" placeholder="${placeholder}" ${df.fieldtype === "Int" ? 'step="1"' : 'step="any"'}>`;
                break;

            case "Date":
                control = `<input type="date" class="form-control" name="${df.fieldname}" value="${this.escape_attr(value)}">`;
                break;

            case "Password":
                control = `<input type="password" class="form-control" name="${df.fieldname}" value="${this.escape_attr(value)}" placeholder="${placeholder}">`;
                break;

            case "Signature":
                return `
                    <div class="frappe-control" data-fieldtype="Signature">
                        ${label}
                        <div class="portal-signature">
                            <canvas class="portal-signature-canvas" id="${this.modal_id}_${df.fieldname}_canvas"></canvas>
                            <button type="button" class="portal-signature-clear" data-target="${this.escape_attr(df.fieldname)}">Clear</button>
                        </div>
                        ${description}
                    </div>`;

            default: // Data and anything else falls back to a text input
                control = `<input type="text" class="form-control" name="${df.fieldname}" value="${this.escape_attr(value)}" placeholder="${placeholder}">`;
        }

        return `<div class="frappe-control" data-fieldtype="${this.escape_attr(df.fieldtype || "Data")}">${label}${control}${description}</div>`;
    }

    get_select_options(df) {
        // Accept options as a newline-separated string, an array of strings, or an array of {label, value}
        let options = df.options || [];
        if (typeof options === "string") {
            options = options.split("\n");
        }
        return options.map(opt =>
            typeof opt === "object" ? opt : { label: opt, value: opt }
        );
    }

    // ---- Signature pad ---------------------------------------------------------------------------

    init_signature_pads() {
        // Must run after the dialog is visible, so the canvas has real (non-zero) dimensions
        Object.values(this.fields_dict).forEach(field => {
            if (field.df.fieldtype === "Signature" && !field.signature) {
                this.init_signature_pad(field);
            }
        });
    }

    init_signature_pad(field) {
        const canvas = field.$canvas[0];
        const ctx = canvas.getContext("2d");

        // Scale the backing store to the device pixel ratio so the line is crisp and tracks the
        // cursor/finger exactly on HiDPI/retina and mobile screens.
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#1f272e";

        let drawing = false;
        let is_empty = true;

        const point = (e) => {
            const r = canvas.getBoundingClientRect();
            const src = e.touches && e.touches.length ? e.touches[0] : e;
            return { x: src.clientX - r.left, y: src.clientY - r.top };
        };
        const mark = () => { is_empty = false; field.$canvas.removeClass("is-invalid"); };
        const start = (e) => {
            e.preventDefault();
            drawing = true;
            const p = point(e);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            // Draw a zero-length segment so a single click/tap leaves a dot (round line cap) instead
            // of requiring the user to drag before anything appears.
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            mark();
        };
        const move = (e) => { if (!drawing) return; e.preventDefault(); const p = point(e); ctx.lineTo(p.x, p.y); ctx.stroke(); mark(); };
        const end = () => { drawing = false; };

        canvas.addEventListener("mousedown", start);
        canvas.addEventListener("mousemove", move);
        document.addEventListener("mouseup", end);
        canvas.addEventListener("touchstart", start, { passive: false });
        canvas.addEventListener("touchmove", move, { passive: false });
        canvas.addEventListener("touchend", end);

        field.signature = {
            clear: () => { ctx.clearRect(0, 0, canvas.width, canvas.height); is_empty = true; },
            is_empty: () => is_empty,
            // Matches frappe's Signature field output: a base64 PNG data URI (empty string if unsigned)
            to_data_url: () => (is_empty ? "" : canvas.toDataURL("image/png")),
            destroy: () => document.removeEventListener("mouseup", end),
        };
    }

    // ---- Values ----------------------------------------------------------------------------------

    get_field(fieldname) {
        return this.fields_dict[fieldname];
    }

    get_value(fieldname) {
        const field = this.fields_dict[fieldname];
        if (!field) return undefined;
        if (field.df.fieldtype === "Signature") {
            return field.signature ? field.signature.to_data_url() : "";
        }
        if (field.df.fieldtype === "Check") {
            return field.$input.is(":checked") ? 1 : 0;
        }
        const val = field.$input.val();
        return typeof val === "string" ? val.trim() : val;
    }

    set_value(fieldname, value) {
        const field = this.fields_dict[fieldname];
        if (!field) return;
        if (field.df.fieldtype === "Check") {
            field.$input.prop("checked", !!value);
        } else {
            field.$input.val(value);
        }
    }

    set_values(values) {
        Object.keys(values || {}).forEach(fieldname => this.set_value(fieldname, values[fieldname]));
    }

    /**
     * Returns the validated form values, or null if a required field is missing. Required fields
     * that are empty are flagged with the `is-invalid` class.
     */
    get_values() {
        const values = {};
        let is_valid = true;

        this.fields.forEach(df => {
            if (!df.fieldname || df.fieldtype === "Heading" || df.fieldtype === "HTML") return;

            const value = this.get_value(df.fieldname);
            const field = this.fields_dict[df.fieldname];
            const $target = field.$input || field.$canvas;

            if (df.reqd && df.fieldtype !== "Check" && (value === "" || value == null)) {
                $target && $target.addClass("is-invalid");
                is_valid = false;
            } else {
                $target && $target.removeClass("is-invalid");
                values[df.fieldname] = value;
            }
        });

        return is_valid ? values : null;
    }

    // ---- Actions ---------------------------------------------------------------------------------

    set_primary_action(label, fn) {
        this.primary_action_label = label;
        this.primary_action = fn;
        $(`#${this.modal_id}_primary`).text(label);
    }

    set_secondary_action(fn, label) {
        this.secondary_action = fn;
        if (label) {
            this.secondary_action_label = label;
            this.$wrapper.find(".modal-footer .portal-dialog-close").text(label);
        }
    }

    bind_events() {
        const me = this;

        $(`#${this.modal_id}_primary`).on("click", function () {
            if (!me.primary_action) return;
            const values = me.get_values();
            if (!values) return;
            me.primary_action.call(me, values);
        });

        this.$wrapper.on("click", ".portal-dialog-close", function () {
            if (me.secondary_action) me.secondary_action.call(me);
            me.hide();
        });

        // Clear a signature pad
        this.$wrapper.on("click", ".portal-signature-clear", function () {
            const field = me.fields_dict[$(this).data("target")];
            if (field && field.signature) field.signature.clear();
            if (field && field.$canvas) field.$canvas.removeClass("is-invalid");
        });

        // Close when clicking the dimmed area outside the dialog panel
        this.$wrapper.on("click", function (e) {
            if (e.target === me.$wrapper[0]) me.hide();
        });

        // Submit on Enter (except in textareas), close on Escape
        this.$wrapper.on("keydown", function (e) {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault();
                $(`#${me.modal_id}_primary`).click();
            } else if (e.key === "Escape") {
                me.hide();
            }
        });
    }

    // ---- Visibility ------------------------------------------------------------------------------

    show() {
        window.cur_dialog = this;
        frappe.ui.open_dialogs.push(this);

        $("body").css("overflow", "hidden");
        this.$wrapper.css("display", "block");

        setTimeout(() => {
            this.$backdrop.addClass("show");
            // Size and wire up any signature pads now that the canvas has real dimensions
            this.init_signature_pads();
            // Focus the first editable control, matching frappe's behaviour
            this.$form.find("input, select, textarea").not('[type="checkbox"]').first().focus();
        }, 10);

        return this;
    }

    hide() {
        const me = this;
        this.$dialog.removeClass("portal-animate-in").addClass("portal-animate-out");
        this.$backdrop.removeClass("show");
        this.$dialog.one("animationend", () => me.clear_dom_elements());
    }

    clear_dom_elements() {
        // Detach any signature pad's document-level listeners before removing the DOM
        Object.values(this.fields_dict).forEach(field => {
            if (field.signature && field.signature.destroy) field.signature.destroy();
        });

        this.$wrapper.remove();
        this.$backdrop.remove();

        const dialogs = frappe.ui.open_dialogs;
        const idx = dialogs.indexOf(this);
        if (idx > -1) dialogs.splice(idx, 1);
        window.cur_dialog = dialogs.length ? dialogs[dialogs.length - 1] : null;

        if (dialogs.length === 0) $("body").css("overflow", "auto");

        this.on_hide && this.on_hide();
    }

    // ---- Helpers ---------------------------------------------------------------------------------

    escape_html(value) {
        if (value == null) return "";
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    escape_attr(value) {
        return this.escape_html(value).replace(/"/g, "&quot;");
    }
}

window.PortalDialog = PortalDialog;
